/**
 * Damage <-> Quote line projection (client)
 *
 * The single source of truth for a quote request is:
 *   - the damage `parts` model (nested, grouped by zone) held in `vehicle_damage`
 *   - plus a set of quote-only "extra" lines (allowances, manual non-damage lines,
 *     imported materials/outwork) held alongside the projection in `quotes.lines`.
 *
 * The quote a user sees is `partsToLines(parts) ++ extras`. This module owns that
 * projection and its pricing, mirroring the server calculator so a quote drafted in
 * the UI matches one generated by generate-quote-from-damage.
 *
 * IMPORTANT: keep the pricing rules here in lockstep with
 * functions/generate-quote-from-damage/quote-calculator.js. The damage buttons in
 * DamagePartsList already mirror the constants below (VEHICLE_TYPE_SUFFIX,
 * PAINT_SIZE_SUFFIX) — this file is the shared home for them.
 */

// Vehicle class applies to labour hours only. Each class resolves to the first
// suffix in its chain that the insurer line carries a value for. Mirrors
// VEHICLE_FIELD_SUFFIX in quote-calculator.js.
export const VEHICLE_TYPE_SUFFIX = {
  standard: ['Standard'],
  european: ['European', 'Standard'],
  luxury: ['European', 'Standard'],
  commercial: ['Standard'],
};

// Paint is split by panel size instead of vehicle class. Mirrors PAINT_SIZE_SUFFIX
// in quote-calculator.js.
export const PAINT_SIZE_SUFFIX = {
  small: 'Small',
  standard: '',
  medium: '',
  large: 'Large',
};

const round2 = (value) => Math.round((Number(value) || 0) * 100) / 100;

// ── Quantity ─────────────────────────────────────────────────────────────────
// The guides price ONE line for a fitting a vehicle carries several of — one
// "Bumper Mould" row against four chrome mouldings, one "Badges Per Badge
// Complete" against a badge on each guard. The count is the only thing that
// bills the rest, so a part carries it and every line it produces is scaled by
// it.
//
// Mirrors clampQuantity in functions/ai-assess-part-damage/assess-core.js and
// scaleForQuantity in functions/generate-quote-from-damage/quote-calculator.js.
// Keep all three in step: a quantity that prices differently here than on the
// server is a quote that changes when it is regenerated.

/** The upper end of the clamp. Six of one fitting is already an unusual car. */
export const MAX_PART_QUANTITY = 6;

/**
 * A quantity the pricing can be trusted with.
 *
 * Anything absent, unparseable or at or below one means one — the
 * overwhelmingly common case. Fractions round down because half an arch
 * moulding is not a thing a quote bills.
 */
export function clampQuantity(value) {
  const n = Math.floor(Number(value));
  if (!Number.isFinite(n) || n <= 1) return 1;
  return Math.min(n, MAX_PART_QUANTITY);
}

/**
 * Bill a per-item guide line as many times as the car carries the item.
 *
 * Applied on the way out rather than inside each operation's pricing: a
 * quantity multiplies every operation the part attracts — the strip, the paint,
 * the beat and the part itself — so scaling the finished lines covers all of
 * them once. Rounded because the multiply is where binary floating point shows:
 * three badges at 0.3h came out as 0.8999999999999999 on the server before this
 * was rounded there.
 *
 * The line keeps its quantity so the quote can show "x4" rather than an
 * unexplained four-fold labour figure.
 */
function scaleForQuantity(lines, quantity) {
  const n = clampQuantity(quantity);
  if (n <= 1) return lines;
  const SCALED = ['hours', 'amount', 'partsCost'];
  return lines.map((line) => {
    const out = { ...line, quantity: n };
    for (const field of SCALED) {
      if (typeof out[field] === 'number') out[field] = round2(out[field] * n);
    }
    return out;
  });
}

/** True for a child part (access / blend / replacement) rather than a primary. */
export function isChildPart(part) {
  return Boolean(part && (part.forAccess || part.isChild || part.forBlend));
}

// ── Parts pricing: cost + markup ─────────────────────────────────────────────
// A replacement part is quoted as what the shop pays (cost) plus the insurer's
// markup, so the charge-out price is always derived and never hand-typed. Most
// insurers set one markup; IAG sets one per part type (OEM 10%, aftermarket /
// exchange / used 20%), so the type is what the assessor picks and the
// percentage follows from it.
//
// The types live on the insurer config as `partMarkups` — a JSON array so the
// list can be changed in the static data without a code change:
//   [{ code, label, markupPercent, isDefault }]

// A percentage that was actually supplied. Guards against Number(null) === 0,
// which would quietly turn a missing markup into "sell at cost".
const isPercent = (v) => v != null && v !== '' && Number.isFinite(Number(v));

/**
 * Normalise an insurer config's `partMarkups` into a usable list. Accepts the
 * raw JSON string as stored, or an already-parsed array. Entries missing a code
 * or a numeric percentage are dropped rather than silently pricing at 0.
 */
export function parsePartMarkups(config) {
  const raw = config && typeof config === 'object' && !Array.isArray(config)
    ? config.partMarkups
    : config;
  let list = raw;
  if (typeof raw === 'string') {
    try {
      list = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(list)) return [];
  return list
    .filter((m) => m && m.code && isPercent(m.markupPercent))
    .map((m) => ({
      code: String(m.code),
      label: m.label ? String(m.label) : String(m.code),
      markupPercent: Number(m.markupPercent),
      isDefault: Boolean(m.isDefault),
    }));
}

/**
 * The markup in force for a part: an explicit percentage override wins, then the
 * selected type, then the insurer's default type, then no markup at all.
 *
 * The type survives an override — it says what kind of part this is, which is
 * still true when the percentage has been set by hand — but only a type the
 * assessor actually chose; an overridden line does not inherit the default.
 *
 * A stored type that the insurer no longer offers (config changed, or the quote
 * moved to another insurer) falls back to the default rather than pricing at the
 * stale percentage.
 *
 * @returns {{ code: string|null, label: string|null, percent: number, isOverride: boolean }}
 */
export function resolvePartMarkup(part, markups) {
  const list = Array.isArray(markups) ? markups : [];
  const selected = part?.partsMarkupCode
    ? list.find((m) => m.code === part.partsMarkupCode)
    : null;
  const override = part?.partsMarkupOverride;
  if (isPercent(override)) {
    return {
      code: selected?.code ?? null,
      label: selected?.label ?? null,
      percent: Number(override),
      isOverride: true,
    };
  }
  const chosen = selected || list.find((m) => m.isDefault) || null;
  return {
    code: chosen?.code ?? null,
    label: chosen?.label ?? null,
    percent: chosen ? chosen.markupPercent : 0,
    isOverride: false,
  };
}

/** What the shop paid for a part. `partsCostOverride` is the pre-markup name. */
export function partsCostOf(part) {
  const cost = part?.partsCost ?? part?.partsCostOverride ?? null;
  if (cost == null || cost === '') return null;
  const n = Number(cost);
  return Number.isFinite(n) ? n : null;
}

/** Charge-out price for a part: cost plus markup. */
export function partsPrice(cost, markupPercent) {
  if (cost == null) return 0;
  return round2(Number(cost) * (1 + (Number(markupPercent) || 0) / 100));
}

/**
 * The unified quote line shape produced from a damage part.
 * `role` says what the line represents within its damage group; `groupId` is the
 * primary part it belongs to (so both the quote and damage views can find each
 * other), and `parentId` links a child (blend/access) to the part it hangs off.
 */
function makeLine(part, role, category, fields) {
  return {
    id: `${part.id}::${role}`,
    role,
    category,
    description: fields.description ?? part.part ?? '',
    part: part.part ?? '',
    zone: part.zone ?? null,
    groupId: part.id,
    // Every part the line stands for, when duplicates were collapsed into it (see
    // dedupeParts). Only present on a merged line; use linePartIds() to read it.
    ...(part.mergedPartIds?.length ? { groupIds: [part.id, ...part.mergedPartIds] } : null),
    parentId: part.parent ?? null,
    lineAllowanceId: part.lineAllowanceId ?? null,
    approach: part.approach ?? null,
    hours: fields.hours ?? null,
    amount: round2(fields.amount ?? 0),
    report: Boolean(part.report),
    paintScope: fields.paintScope ?? null,
    paintSize: fields.paintSize ?? null,
    source: part.source === 'panel-quote' ? 'panel-quote' : 'damage',
    damageManaged: true,
    ...fields.extra,
  };
}

/**
 * Effective values for a part: a manual override wins, else the insurer-line
 * default resolved by the caller (via `getDefaults`).
 */
function effectiveValues(part, getDefaults) {
  const defaults = (getDefaults && getDefaults(part)) || {};
  return {
    rrHours: part.removeRefitHoursOverride ?? defaults.defaultRemoveRefitHours ?? null,
    repairHours: part.repairHoursOverride ?? defaults.defaultRepairHours ?? null,
    paintCost: part.paintCostOverride ?? defaults.defaultPaintCost ?? null,
  };
}

/**
 * Project ONE damage part into its quote line(s). Mirrors createQuoteLines in
 * quote-calculator.js:
 *   - forAccess  -> a single R&R line
 *   - forBlend   -> a single "Blend - {part}" Painting line
 *   - otherwise  -> one line per active operation (R&R / Panel Beating / Painting)
 *                   plus a Parts line when the approach is "replace"
 *
 * @param {Object} part - damage part
 * @param {Object} ctx - { getDefaults, labourRate, blendUnitPrice, blendUnits }
 * @returns {Array} unified quote lines
 */
export function partToLines(part, ctx = {}) {
  if (!part) return [];
  const labourRate = Number(ctx.labourRate) || 0;
  const { rrHours, repairHours, paintCost } = effectiveValues(part, ctx.getDefaults);

  // Access-only parts -> a single R&R line
  if (part.forAccess) {
    const hours = rrHours;
    return scaleForQuantity([
      makeLine(part, 'access', 'Remove & Refit', {
        hours: hours ?? null,
        amount: hours != null ? round2(hours * labourRate) : 0,
        extra: { accessParentId: part.parent ?? null },
      }),
    ], part.quantity);
  }

  // Blend parts -> a single Painting line priced off the blend rate
  if (part.forBlend) {
    const blendRate = part.blendRateOverride ?? ctx.blendUnitPrice ?? null;
    const blendUnits = Number(ctx.blendUnits) > 0 ? Number(ctx.blendUnits) : 1;
    const total = blendRate != null ? round2(blendRate * blendUnits) : 0;
    return scaleForQuantity([
      makeLine(part, 'blend', 'Painting', {
        description: `Blend - ${part.part ?? ''}`.trim(),
        amount: total,
        extra: { blend: true, blendParentId: part.parent ?? null },
      }),
    ], part.quantity);
  }

  const lines = [];
  const approach = String(part.approach || '').toLowerCase();

  if (part.removeRefit) {
    lines.push(
      makeLine(part, 'rr', 'Remove & Refit', {
        hours: rrHours ?? null,
        amount: rrHours != null ? round2(rrHours * labourRate) : 0,
      }),
    );
  }

  if (part.panelbeating) {
    lines.push(
      makeLine(part, 'panelbeat', 'Panel Beating', {
        hours: repairHours ?? null,
        amount: repairHours != null ? round2(repairHours * labourRate) : 0,
      }),
    );
  }

  if (part.paint) {
    if (part.blend) {
      // A paint operation flagged as a blend (the panel is blended, not fully
      // refinished) is priced off the blend rate instead of paint units.
      const blendRate = part.blendRateOverride ?? ctx.blendUnitPrice ?? null;
      const blendUnits = Number(ctx.blendUnits) > 0 ? Number(ctx.blendUnits) : 1;
      const total = blendRate != null ? round2(blendRate * blendUnits) : 0;
      lines.push(
        makeLine(part, 'paint', 'Painting', {
          description: `Blend - ${part.part ?? ''}`.trim(),
          amount: total,
          paintScope: part.paintScope || 'outer',
          paintSize: part.paintSize || null,
          extra: { blend: true },
        }),
      );
    } else {
      lines.push(
        makeLine(part, 'paint', 'Painting', {
          amount: paintCost != null ? round2(paintCost) : 0,
          paintScope: part.paintScope || 'outer',
          paintSize: part.paintSize || null,
          extra: { blend: false },
        }),
      );
    }
  }

  // A replacement needs a Parts line. Cost starts blank for the user to fill in;
  // the price is always cost + the insurer's markup, never typed directly.
  if (approach.includes('replace')) {
    const cost = partsCostOf(part);
    const markup = resolvePartMarkup(part, ctx.partMarkups);
    lines.push(
      makeLine(part, 'parts', 'Parts', {
        amount: partsPrice(cost, markup.percent),
        extra: {
          partsCost: cost,
          markupCode: markup.code,
          markupPercent: markup.percent,
          markupIsOverride: markup.isOverride,
        },
      }),
    );
  }

  // A part with no active operation still produces one "Other" line so it is not
  // silently dropped from the quote.
  if (lines.length === 0) {
    lines.push(makeLine(part, 'plain', 'Other', { amount: 0 }));
  }

  return scaleForQuantity(lines, part.quantity);
}

// ── Collapsing duplicate parts ───────────────────────────────────────────────
// The damage model keeps access and blend children under EACH primary: a part
// lists everything that has to come off to reach it, and the assessment view
// shows that list on the part it belongs to. Neighbouring panels therefore name
// the same access part (a rear bumper and a rear panel both need the tow bar off)
// and every painted panel names its neighbours as blends, so the same physical
// job appears several times in the model — by design.
//
// On a quote each of those is one job: the tow bar comes off once, a quarter is
// blended once. The projection is where they collapse, which is what the damage
// assessment has always assumed (see the access-part comment in
// ai-damage-assessment-orchestrator.js). Mirrors the dedupe in
// generate-quote-from-damage.js.

/** Part name as an identity: lowercase, collapsed whitespace. */
function partNameKey(part) {
  return String(part?.part || '').toLowerCase().replace(/\s+/g, ' ').trim();
}

/**
 * Fold one duplicate into another. The union of the work is kept — whichever copy
 * says a panel comes off, it comes off — and hours or costs typed on either copy
 * survive, so a merge never silently reprices a line to the insurer default.
 */
function mergeDuplicateParts(a, b) {
  // A primary is the assessment of the part itself; an access or replacement
  // child is only there to reach it. So the primary keeps its identity, approach
  // and images, and the child folds into it whichever order they arrive in.
  const [keep, folded] = isChildPart(a) && !isChildPart(b) ? [b, a] : [a, b];
  return {
    ...keep,
    removeRefit: Boolean(keep.removeRefit || folded.removeRefit),
    paint: Boolean(keep.paint || folded.paint),
    panelbeating: Boolean(keep.panelbeating || folded.panelbeating),
    removeRefitHoursOverride: keep.removeRefitHoursOverride ?? folded.removeRefitHoursOverride,
    repairHoursOverride: keep.repairHoursOverride ?? folded.repairHoursOverride,
    paintCostOverride: keep.paintCostOverride ?? folded.paintCostOverride,
    blendRateOverride: keep.blendRateOverride ?? folded.blendRateOverride,
    partsCost: partsCostOf(keep) ?? partsCostOf(folded),
    // The max, never the sum. Two copies of one guard both reporting two arch
    // mouldings are one guard carrying two, not four — the counts are competing
    // observations of the same part, so the fuller one wins. Mirrors
    // dedupeAssessments in pipeline-core.js, where summing across panel turns
    // was measured and unlocked zero reference lines.
    quantity: Math.max(clampQuantity(keep.quantity), clampQuantity(folded.quantity)),
    lineAllowanceId: keep.lineAllowanceId || folded.lineAllowanceId,
    mergedPartIds: [...(keep.mergedPartIds || []), ...(folded.mergedPartIds || []), folded.id]
      .filter((id) => id && id !== keep.id),
  };
}

/**
 * Collapse damage parts that describe the same physical part, ready for pricing.
 *
 * Identity is the insurer line the part is matched to, or its name when there is
 * none. Blends are keyed apart from the work itself, because a panel can be both
 * repaired and blended into by a neighbour.
 *
 * Only children collapse. Two primaries with the same name are two lines an
 * assessor deliberately wrote, and merging those would take work off the quote —
 * duplicate primaries are already prevented upstream, where the AI aggregates its
 * findings.
 *
 * @param {Array} parts - flat damage parts
 * @returns {Array} parts with duplicates folded in, in first-seen order
 */
export function dedupeParts(parts) {
  const out = [];
  const byLine = new Map(); // insurer line id -> index in out
  const byName = new Map(); // part name -> index in out

  const keysOf = (part) => {
    const namespace = part.forBlend ? 'blend:' : '';
    const name = partNameKey(part);
    return {
      lineKey: part.lineAllowanceId ? `${namespace}${part.lineAllowanceId}` : null,
      nameKey: name ? `${namespace}${name}` : null,
    };
  };

  for (const part of (parts || []).filter(Boolean)) {
    const { lineKey, nameKey } = keysOf(part);
    let at = lineKey != null && byLine.has(lineKey) ? byLine.get(lineKey) : undefined;
    if (at === undefined && nameKey != null) at = byName.get(nameKey);

    // Unseen, unidentifiable, or a second primary: a line of its own. Existing
    // keys are never repointed, so the first copy stays the one duplicates find.
    if (at === undefined || (!isChildPart(part) && !isChildPart(out[at]))) {
      at = out.length;
      out.push(part);
    } else {
      out[at] = mergeDuplicateParts(out[at], part);
    }
    if (lineKey != null && !byLine.has(lineKey)) byLine.set(lineKey, at);
    if (nameKey != null && !byName.has(nameKey)) byName.set(nameKey, at);
  }

  // A panel painted in its own right is not also blended. The assessment allows
  // both (a bumper being painted is still a blend part of the quarter next to it),
  // but on the quote the paint line covers the panel and the blend would charge
  // for it twice.
  const painted = new Set();
  for (const part of out) {
    if (part.forBlend || !part.paint) continue;
    if (part.lineAllowanceId) painted.add(String(part.lineAllowanceId));
    const name = partNameKey(part);
    if (name) painted.add(name);
  }
  return out.filter((part) => {
    if (!part.forBlend) return true;
    return !(
      (part.lineAllowanceId && painted.has(String(part.lineAllowanceId)))
      || painted.has(partNameKey(part))
    );
  });
}

/** Every damage part a projected line stands for — more than one after a merge. */
export function linePartIds(line) {
  if (Array.isArray(line?.groupIds) && line.groupIds.length) return line.groupIds;
  return line?.groupId ? [line.groupId] : [];
}

/**
 * Project the whole nested damage model (parts grouped by zone) into a flat array
 * of unified quote lines. Iterates every item (primaries AND children), matching
 * the server which treats each damage item independently — save that parts naming
 * the same job are collapsed first (see dedupeParts).
 *
 * @param {Object} partsByZone - { zoneId: [part, ...] }
 * @param {Object} ctx - pricing context (see partToLines)
 * @returns {Array} unified quote lines
 */
export function partsToLines(partsByZone, ctx = {}) {
  if (!partsByZone || typeof partsByZone !== 'object') return [];
  const flat = [];
  Object.keys(partsByZone).forEach((zoneId) => {
    (partsByZone[zoneId] || []).forEach((part) => {
      flat.push({ ...part, zone: part.zone || zoneId });
    });
  });
  const lines = [];
  dedupeParts(flat).forEach((part) => {
    lines.push(...partToLines(part, ctx));
  });
  return lines;
}

/** Line item amounts are GST-exclusive. Compute quote totals from any line list. */
export function computeTotals(lines, gstRate = 0.15) {
  const subtotal = (lines || []).reduce((sum, l) => sum + (Number(l.amount) || 0), 0);
  const gst = subtotal * gstRate;
  return {
    subtotal: round2(subtotal),
    gst: round2(gst),
    total: round2(subtotal + gst),
  };
}

/**
 * Split stored quote lines back into quote-only "extras" (kept) vs derived lines
 * (dropped — regenerated by the projection / allowance computation). Drops damage
 * lines (incl. legacy source 'damage-assessment' / 'blend' / damageItemId) and
 * computed insurer-allowance lines so nothing double-counts on first load.
 */
export function splitStoredLines(lines) {
  const arr = Array.isArray(lines) ? lines : [];
  const isDerived = (l) =>
    l.damageManaged === true ||
    l.computed === true ||
    l.source === 'damage' ||
    l.source === 'damage-assessment' ||
    l.source === 'blend' ||
    l.source === 'allowance' ||
    Boolean(l.damageItemId) ||
    Boolean(l.groupId);
  return { extras: arr.filter((l) => !isDerived(l)) };
}

// Labour, for the purposes of an allowance that is a percentage of it. Every
// insurer schedule defines its materials allowance this way — Ando "materials 5%
// of labour", FMG "5% of the complete labour costs", IAG "5% of the panel labour
// content of the assessment". Painting is deliberately not in the set: it is not
// charged as labour here, it is paint units at a paint rate, and paint materials
// are a separate allowance in every schedule.
const LABOUR_CATEGORIES = new Set(['Remove & Refit', 'Panel Beating']);

/**
 * The lines a percentage allowance is a percentage OF.
 *
 * `calculationType` on the insurer allowance has always declared this, but no
 * calculator read it — every percentage came off the whole quote, which made
 * "5% of labour" 5% of the parts and paint as well. Only the labour basis is
 * modelled so far; every other type keeps the whole-quote base it has always
 * had, rather than being silently repriced onto a base this model can't identify
 * (freight and success fees, for instance, are not line categories here).
 */
function allowanceBasis(allowance, lines) {
  const calculationType = String(allowance?.calculationType || '').toLowerCase();
  if (calculationType === 'percentage_of_labour') {
    return lines.filter((l) => LABOUR_CATEGORIES.has(l.category));
  }
  return lines;
}

/**
 * The editable line that replaces a computed allowance when an assessor overrules
 * it. A calculated allowance is a default, not a rule — but once overridden it
 * must stop recalculating, so it is stored as an ordinary quote-only line that
 * remembers which allowance it stands in for. Removing it restores the
 * calculation (see computeAllowanceLines).
 */
export function allowanceOverrideLine(line, amount) {
  return {
    // Deliberately the same id as the line it replaces: the two never coexist,
    // and keeping it stable means React swaps the row in place rather than
    // remounting it, so the field the assessor is typing into keeps focus.
    id: `allowance-${line.allowanceId}`,
    role: 'allowance',
    source: 'allowance-override',
    allowanceId: line.allowanceId,
    category: line.category || 'Other',
    description: line.description || 'Allowance',
    part: line.part || line.description || 'Allowance',
    hours: null,
    amount: round2(amount),
    report: false,
    damageManaged: false,
  };
}

/**
 * How many painted panels the quote covers. Per-panel allowances (paint
 * protection being the common one) are worth this multiple, so an allowance that
 * used to be silently dropped now prices off the actual assessment.
 *
 * Counts distinct damage groups with a paint line — a panel painted inner and
 * outer is still one panel, and blends are not full panel refinishes.
 */
function countPaintedPanels(lines) {
  const panels = new Set();
  for (const line of lines || []) {
    if (line.role !== 'paint' || line.blend) continue;
    panels.add(line.groupId || line.id);
  }
  return panels.size;
}

/**
 * Compute insurer quote-allowance lines (admin fees, waste, levies) from the
 * current quote subtotal. Mirrors functions/generate-quote-from-damage/
 * allowance-calculator.js. These are the "default lines" seeded on a quote when
 * an insurer is set; they recompute as the quote changes and are not editable.
 *
 * Quantities follow the assessment: a per-panel allowance multiplies by the
 * panels actually being painted, and the insurer's maximum caps it (Ando's paint
 * protection is $45 a panel to a $270 ceiling, i.e. six panels).
 *
 * An allowance the assessor has overruled is skipped: their line is already in
 * `baseLines` and stands in for this one.
 *
 * @param {Array} allowances - insurer_quote_allowances docs
 * @param {Array} baseLines - the priced lines the allowance is a percentage of
 */
export function computeAllowanceLines(allowances, baseLines) {
  if (!Array.isArray(allowances) || allowances.length === 0) return [];
  const all = Array.isArray(baseLines) ? baseLines : [];
  // An override stands in for its allowance, so it neither recalculates nor
  // counts towards the base another allowance is a percentage of — it is an
  // allowance, just one with a number an assessor chose.
  const overridden = new Set(
    all.filter((l) => l.source === 'allowance-override').map((l) => l.allowanceId).filter(Boolean),
  );
  const base = all.filter((l) => l.source !== 'allowance-override');
  const hasPaint = base.some((l) => l.role === 'paint' || l.role === 'blend');
  const hasBody = base.some((l) => ['rr', 'panelbeat', 'access', 'parts'].includes(l.role));
  const paintedPanels = countPaintedPanels(base);

  const out = [];
  for (const a of allowances) {
    if (overridden.has(a.$id)) continue;
    if (a.applyOnlyIfPaintWork && !hasPaint) continue;
    if (a.applyOnlyIfBodyWork && !hasBody) continue;

    let amount = 0;
    let quantity = null;
    if (typeof a.percentage === 'number' && a.percentage) {
      const subtotal = allowanceBasis(a, base).reduce((s, l) => s + (Number(l.amount) || 0), 0);
      amount += subtotal * (a.percentage / 100);
    }
    if (typeof a.fixedAmount === 'number' && a.fixedAmount) amount += a.fixedAmount;
    if (typeof a.perPanelAmount === 'number' && a.perPanelAmount) {
      // No painted panels means the allowance simply doesn't apply to this quote.
      if (paintedPanels === 0) continue;
      quantity = paintedPanels;
      amount += a.perPanelAmount * paintedPanels;
    }

    if (typeof a.maximumAmount === 'number' && a.maximumAmount && amount > a.maximumAmount) {
      amount = a.maximumAmount;
      // Show the quantity the cap actually pays for, not the number claimed.
      if (quantity != null && a.perPanelAmount) quantity = Math.floor(a.maximumAmount / a.perPanelAmount);
    }
    if (typeof a.minimumAmount === 'number' && a.minimumAmount && amount < a.minimumAmount) amount = a.minimumAmount;
    amount = round2(amount);
    if (!amount) continue;

    const name = a.allowanceName || a.allowanceType || 'Allowance';
    out.push({
      id: `allowance-${a.$id}`,
      role: 'allowance',
      computed: true,
      source: 'allowance',
      category: a.quoteCategory || 'Other',
      description: quantity != null ? `${name} × ${quantity}` : name,
      part: name,
      hours: null,
      quantity,
      amount,
      report: false,
      damageManaged: false,
      allowanceId: a.$id,
    });
  }
  return out;
}

/**
 * Which damage-part fields a quote-view edit of a damage-derived line maps back
 * to. Returns a patch to apply to the part, or null when the edit does not map
 * (e.g. editing a Parts line price, which is derived from cost × markup).
 */
export function inversePatchForLineEdit(line, field, value, labourRate = 0) {
  if (!line || !line.damageManaged) return null;
  const rate = Number(labourRate) || 0;
  // Everything shown on a line covering several fittings is already multiplied
  // by the count, so an edited figure has to be divided back out before it is
  // stored — the part holds the per-item value and the projection scales it. Get
  // this wrong and typing the number already on screen squares it.
  const qty = clampQuantity(line.quantity);
  const num = (v) => (v === '' || v == null ? null : Number(v));
  const perItem = (v) => (v == null ? null : (qty > 1 ? round2(v / qty) : v));

  // The count itself. Integers only, 1..MAX_PART_QUANTITY: the guides carry one
  // row whatever the car wears, so this is how the rest get billed.
  if (field === 'quantity') {
    return { quantity: clampQuantity(value) };
  }

  if (field === 'hours') {
    if (line.role === 'rr' || line.role === 'access') {
      return { removeRefitHoursOverride: perItem(num(value)) };
    }
    if (line.role === 'panelbeat') {
      return { repairHoursOverride: perItem(num(value)) };
    }
    return null;
  }

  // Parts are quoted as cost + markup: the cost and the markup are editable, the
  // price that falls out of them is not.
  if (line.role === 'parts') {
    if (field === 'partsCost') return { partsCost: perItem(num(value)) };
    // Picking a type adopts that type's percentage, so any one-off percentage
    // the assessor had typed no longer applies.
    if (field === 'markupCode') return { partsMarkupCode: value || null, partsMarkupOverride: null };
    if (field === 'markupPercent') return { partsMarkupOverride: num(value) };
  }

  if (field === 'amount') {
    if (line.role === 'paint') {
      return { paintCostOverride: perItem(num(value)) };
    }
    if (line.role === 'blend') {
      return { blendRateOverride: perItem(num(value)) };
    }
    // R&R / Panel Beating amounts are derived from hours × rate; convert back.
    if ((line.role === 'rr' || line.role === 'access') && rate > 0) {
      const hours = perItem(num(value));
      return { removeRefitHoursOverride: hours == null ? null : round2(hours / rate) };
    }
    if (line.role === 'panelbeat' && rate > 0) {
      const hours = perItem(num(value));
      return { repairHoursOverride: hours == null ? null : round2(hours / rate) };
    }
    return null;
  }

  if (field === 'description') {
    return { part: value };
  }

  if (field === 'report') {
    return { report: Boolean(value) };
  }

  return null;
}

/**
 * Which damage-part operation flag a quote-view deletion of a damage-derived line
 * toggles off. For blend/access this removes the child part entirely (handled by
 * the caller via parentId); for a role on a primary it turns the operation off.
 */
export function operationForRole(role) {
  switch (role) {
    case 'rr':
      return 'removeRefit';
    case 'panelbeat':
      return 'panelbeating';
    case 'paint':
      return 'paint';
    default:
      return null;
  }
}
